import { useState } from "react";
import { Link } from "react-router-dom";
import { fmtINR } from "@/lib/format";
import useManufacturers from "@/hooks/useManufacturers";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Building2, Package, Search, Star, ChevronLeft, FolderTree } from "lucide-react";

function ProductCard({ p, companyName }) {
  return (
    <Link to={`/products/${p.id}`} data-testid={`catalog-product-${p.id}`}>
      <Card className="rounded-3xl border-border/60 shadow-soft card-lift h-full">
        <CardContent className="p-4">
          <div className="h-28 rounded-2xl bg-muted/40 grid place-items-center overflow-hidden mb-3">
            {(p.images || [])[0]
              ? <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" />
              : <Package className="h-8 w-8 text-muted-foreground/50" />}
          </div>
          <div className="flex items-start gap-1.5">
            <p className="font-display font-extrabold text-sm leading-snug flex-1">{p.name}</p>
            {p.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400 shrink-0 mt-0.5" />}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {[companyName, p.sku && `Code ${p.sku}`].filter(Boolean).join(" · ")}
          </p>
          {p.short_description && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{p.short_description}</p>}
          {p.unit_price ? <p className="font-display font-black text-primary-700 mt-2">{fmtINR(p.unit_price)}</p> : null}
        </CardContent>
      </Card>
    </Link>
  );
}

export const CatalogBrowse = ({ products, categories }) => {
  const mfrs = useManufacturers();
  const [mode, setMode] = useState("company");   // company | category
  const [q, setQ] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [catId, setCatId] = useState("");
  const [subId, setSubId] = useState("");

  const visible = products.filter((p) => p.is_active !== false && p.show_on_website !== false);
  const activeCompanies = mfrs.filter((m) => m.is_active !== false);
  const mfrById = mfrs.reduce((a, m) => { a[m.id] = m; return a; }, {});
  const catById = categories.reduce((a, c) => { a[c.id] = c; return a; }, {});
  const tops = categories.filter((c) => !c.parent_id && c.is_active !== false);

  const sortProducts = (arr) => [...arr].sort((a, b) =>
    (b.featured === true) - (a.featured === true)
    || (a.display_order || 0) - (b.display_order || 0)
    || (a.name || "").localeCompare(b.name || ""));

  const searchNeedle = q.trim().toLowerCase();
  const searched = searchNeedle
    ? visible.filter((p) =>
        (p.name || "").toLowerCase().includes(searchNeedle)
        || (p.sku || "").toLowerCase().includes(searchNeedle)
        || (p.short_description || "").toLowerCase().includes(searchNeedle)
        || (p.description || "").toLowerCase().includes(searchNeedle))
    : null;

  const reset = () => { setCompanyId(""); setCatId(""); setSubId(""); };

  // Products after the drill-down selection
  let drilled = visible;
  if (companyId) drilled = drilled.filter((p) => p.manufacturer_id === companyId);
  if (catId) drilled = drilled.filter((p) => p.category_id === catId || p.subcategory_id === catId);
  if (subId) drilled = drilled.filter((p) => p.subcategory_id === subId);

  const countByCompany = visible.reduce((a, p) => { if (p.manufacturer_id) a[p.manufacturer_id] = (a[p.manufacturer_id] || 0) + 1; return a; }, {});
  const countByCat = visible.reduce((a, p) => {
    if (p.category_id) a[p.category_id] = (a[p.category_id] || 0) + 1;
    return a;
  }, {});

  // Chips for the second drill level
  const companyCats = companyId
    ? tops.filter((c) => visible.some((p) => p.manufacturer_id === companyId && p.category_id === c.id))
    : [];
  const catCompanies = catId && !companyId
    ? activeCompanies.filter((m) => visible.some((p) => p.category_id === catId && p.manufacturer_id === m.id))
    : [];
  const subcats = catId
    ? categories.filter((c) => c.parent_id === catId && c.is_active !== false && drilled.concat(visible.filter((p) => p.category_id === catId)).some((p) => p.subcategory_id === c.id))
    : [];

  const grid = (arr) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {sortProducts(arr).map((p) => <ProductCard key={p.id} p={p} companyName={mfrById[p.manufacturer_id]?.name} />)}
      {arr.length === 0 && <p className="col-span-full text-center text-sm text-muted-foreground py-10">No products here yet.</p>}
    </div>
  );

  return (
    <div data-testid="catalog-browse">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products by name, code or keyword…" className="pl-10 h-11 rounded-2xl bg-muted/40" data-testid="catalog-search" />
        </div>
        <div className="inline-flex rounded-2xl border border-border/60 overflow-hidden bg-card" data-testid="catalog-mode-toggle">
          <button type="button" onClick={() => { setMode("company"); reset(); }} className={`px-3 h-11 text-xs font-bold ${mode === "company" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="catalog-mode-company">By Company</button>
          <button type="button" onClick={() => { setMode("category"); reset(); }} className={`px-3 h-11 text-xs font-bold ${mode === "category" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="catalog-mode-category">By Category</button>
        </div>
      </div>

      {searched ? (
        <>
          <p className="text-xs text-muted-foreground mb-3">{searched.length} result{searched.length === 1 ? "" : "s"} for “{q.trim()}”</p>
          {grid(searched)}
        </>
      ) : mode === "company" && !companyId ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {activeCompanies.map((m) => (
            <button type="button" key={m.id} onClick={() => { setCompanyId(m.id); setCatId(""); setSubId(""); }} className="text-left" data-testid={`catalog-company-${m.id}`}>
              <Card className="rounded-3xl border-border/60 shadow-soft card-lift h-full">
                <CardContent className="p-5 text-center">
                  <div className="h-16 w-16 mx-auto rounded-2xl border border-border/60 bg-muted/30 grid place-items-center overflow-hidden mb-3">
                    {m.logo_url ? <img src={m.logo_url} alt={m.name} className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
                  </div>
                  <p className="font-display font-extrabold text-sm">{m.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{countByCompany[m.id] || 0} product{(countByCompany[m.id] || 0) === 1 ? "" : "s"}</p>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      ) : mode === "category" && !catId ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {tops.map((c) => (
            <button type="button" key={c.id} onClick={() => { setCatId(c.id); setSubId(""); setCompanyId(""); }} className="text-left" data-testid={`catalog-category-${c.id}`}>
              <Card className="rounded-3xl border-border/60 shadow-soft card-lift h-full">
                <CardContent className="p-5 text-center">
                  <div className="h-12 w-12 mx-auto rounded-2xl bg-primary-50 text-primary-700 grid place-items-center mb-3"><FolderTree className="h-5 w-5" /></div>
                  <p className="font-display font-extrabold text-sm">{c.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{countByCat[c.id] || 0} product{(countByCat[c.id] || 0) === 1 ? "" : "s"}</p>
                </CardContent>
              </Card>
            </button>
          ))}
          {tops.length === 0 && <p className="col-span-full text-center text-sm text-muted-foreground py-10">No categories yet — ask your admin to create them under the Categories tab.</p>}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700" data-testid="catalog-back">
              <ChevronLeft className="h-3.5 w-3.5" /> All {mode === "company" ? "companies" : "categories"}
            </button>
            {companyId && mfrById[companyId] && (
              <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 text-xs">{mfrById[companyId].name}</Badge>
            )}
            {catId && catById[catId] && (
              <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 text-xs">{catById[catId].name}</Badge>
            )}
          </div>

          {/* Second-level chips */}
          {mode === "company" && companyCats.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="catalog-company-cat-chips">
              <button type="button" onClick={() => { setCatId(""); setSubId(""); }} className={`px-3 h-8 rounded-2xl border text-xs font-semibold ${!catId ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"}`}>All</button>
              {companyCats.map((c) => (
                <button type="button" key={c.id} onClick={() => { setCatId(c.id); setSubId(""); }} className={`px-3 h-8 rounded-2xl border text-xs font-semibold ${catId === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"}`}>{c.name}</button>
              ))}
            </div>
          )}
          {mode === "category" && catCompanies.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="catalog-cat-company-chips">
              <button type="button" onClick={() => setCompanyId("")} className={`px-3 h-8 rounded-2xl border text-xs font-semibold ${!companyId ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"}`}>All companies</button>
              {catCompanies.map((m) => (
                <button type="button" key={m.id} onClick={() => setCompanyId(m.id)} className={`px-3 h-8 rounded-2xl border text-xs font-semibold ${companyId === m.id ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"}`}>{m.name}</button>
              ))}
            </div>
          )}
          {subcats.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="catalog-subcat-chips">
              <button type="button" onClick={() => setSubId("")} className={`px-3 h-8 rounded-2xl border text-xs font-semibold ${!subId ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"}`}>All</button>
              {subcats.map((c) => (
                <button type="button" key={c.id} onClick={() => setSubId(c.id)} className={`px-3 h-8 rounded-2xl border text-xs font-semibold ${subId === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground"}`}>{c.name}</button>
              ))}
            </div>
          )}

          {grid(drilled)}
        </>
      )}
    </div>
  );
};
